import classNames from "classnames";
import { useCallback, useState } from "react";
import AutoSuggest from "react-autosuggest";
import { useSelector } from "react-redux";

import { CptCode, CptInput } from "custom-models";

import { useForcedModifier } from "hooks/useForcedModifier";
import ApplicationState from "store/ApplicationState";

type CPTInputLineProps = {
  index?: number;
  cpt: CptInput;
  onChangeCpt: Function;
  availableCptCodes: CptCode[];
  placeholder?: string;
  forcedModifier?: string;
};

const CPTInputLine = (props: CPTInputLineProps) => {
  const features = useSelector(({ sys }: ApplicationState) => sys.features);
  const {
    forcedModifier: hardcodedForcedModifier,
    forceTCOnly,
    forcePCOnly,
  } = useForcedModifier();
  // FIXME: We want to blend these until *everything* is on Generic Rules
  const forcedModifier = props.forcedModifier ?? hardcodedForcedModifier;

  const [cptCodeSuggestions, setCptCodeSuggestions] = useState<CptCode[]>([]);

  const filterCptCodesbySuggestion = (text: string) => {
    const suggestions = (props.availableCptCodes || []).filter(
      (cptcode: CptCode) =>
        cptcode.code.startsWith(text) ||
        (cptcode.short_description || "").indexOf(text) >= 0 ||
        (cptcode.description || "").indexOf(text) >= 0
    );
    return suggestions.length === 1 && suggestions[0].code === text
      ? []
      : suggestions;
  };

  const checkError = (text: string) => {
    const error = !props.availableCptCodes.some(
      (availableCode: CptCode) => availableCode.code === text
    );
    return error;
  };

  const handleOnChangeText = useCallback(
    (newValue: string) => {
      const code = props.availableCptCodes.find((cpt) => cpt.code === newValue);

      props.onChangeCpt({
        ...props.cpt,
        ...{
          code: code
            ? {
                ...code,
                modifier: forcedModifier ?? code?.modifier,
              }
            : { code: newValue },
          error: checkError(newValue),
        },
      });
    },
    [props.availableCptCodes, forcedModifier]
  );

  const handleOnChangeQuantity = (e: any) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) {
      props.onChangeCpt({ ...props.cpt, quantity: value });
    }
  };

  const code_input_identifier = `CPTInputLine__code-input-${props.index}`;
  const number_input_identifier = `CPTInputLine__number-input-${props.index}`;

  return (
    <div
      className={classNames("Sidebar__group-container", "CPTInputLine", {
        "input__container--error": props.cpt.error,
        input__error: props.cpt.error,
        "input__error--bottom": props.cpt.error,
      })}
    >
      <label
        className="Sidebar__group-container"
        htmlFor={code_input_identifier}
      >
        {/* <span className="input__label--center">Code:</span> */}
        <AutoSuggest
          suggestions={cptCodeSuggestions}
          getSuggestionValue={(suggestion: CptCode) => suggestion.code}
          inputProps={{
            id: code_input_identifier,
            name: code_input_identifier,
            className: code_input_identifier,
            placeholder: props.placeholder,
            value: props.cpt.code.code,
            onChange: (_, { newValue }) => {
              handleOnChangeText(newValue);
            },
          }}
          renderSuggestion={(suggestion) => (
            <div>
              <strong>{suggestion.code}</strong>
              <p>{suggestion.short_description || suggestion.description}</p>
            </div>
          )}
          onSuggestionsClearRequested={() => {
            setCptCodeSuggestions([]);
          }}
          onSuggestionsFetchRequested={({ value }) => {
            setCptCodeSuggestions(filterCptCodesbySuggestion(value));
          }}
        />
      </label>
      {features?.cptCodeModifiers && (
        <>
          <label className="Sidebar__group-container input__container--checkbox">
            <input
              type="checkbox"
              checked={
                forceTCOnly ||
                props.forcedModifier === "TC" ||
                props.cpt.code.modifier === "TC" ||
                false
              }
              name="tc_only"
              value="tc_only"
              disabled={
                forceTCOnly ||
                forcePCOnly ||
                !!props.forcedModifier ||
                !props.cpt.code.code
              }
              onChange={(e) => {
                props.onChangeCpt({
                  ...props.cpt,
                  code: {
                    ...props.cpt.code,
                    modifier:
                      forceTCOnly ||
                      props.forcedModifier === "TC" ||
                      e.target.checked
                        ? "TC"
                        : undefined,
                  },
                });
              }}
            />
            &nbsp;TC
          </label>
          <label className="Sidebar__group-container input__container--checkbox">
            <input
              type="checkbox"
              checked={
                forcePCOnly ||
                props.forcedModifier === "26" ||
                props.cpt.code.modifier === "26" ||
                false
              }
              name="pc_only"
              value="pc_only"
              disabled={
                forceTCOnly ||
                forcePCOnly ||
                !!props.forcedModifier ||
                !props.cpt.code.code
              }
              onChange={(e) => {
                props.onChangeCpt({
                  ...props.cpt,
                  code: {
                    ...props.cpt.code,
                    modifier:
                      forcePCOnly ||
                      props.forcedModifier === "26" ||
                      e.target.checked
                        ? "26"
                        : undefined,
                  },
                });
              }}
            />
            &nbsp;PC
          </label>
        </>
      )}
      <label
        htmlFor={number_input_identifier}
        className="Sidebar__group-container input__container--number"
      >
        <span className="input__label--center">Qty:</span>
        <input
          type="number"
          id={number_input_identifier}
          name={number_input_identifier}
          className={number_input_identifier}
          onChange={handleOnChangeQuantity}
          value={props.cpt.quantity || 1}
          min="1"
        />
      </label>
    </div>
  );
};

export default CPTInputLine;
